import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert, Card, CardContent, CardHeader, TextField } from '@mui/material';
import * as z from 'zod';
import LoaderOverlay from '@/components/ui/LoaderOverlay/LoaderOverlay.tsx';
import { useAuthContext } from '@/context/AuthContext.tsx';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton.tsx';

const resetPasswordSchema = z.object({
  email: z.string().min(1, { message: 'This field is required.' }).email('Invalid email'),
});

type ResetPasswordSchema = z.infer<typeof resetPasswordSchema>;

const ForgotPasswordPage: FC = () => {
  const { t } = useTranslation();
  const { control, handleSubmit } = useForm<ResetPasswordSchema>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      email: '',
    },
  });
  const {
    sendResetPasswordLinkQuery: {
      mutateAsync: resetPassword,
      isPending: isLoadingResetPassword,
      error: errorResetPassword,
      isError: isErrorResetPassword,
      isSuccess: isSuccessResetPassword,
    },
  } = useAuthContext();

  const isLoading = isLoadingResetPassword;

  const handleSendResetPasswordLink = (data: ResetPasswordSchema) => {
    void (async () => {
      try {
        if (isLoading) return;

        await resetPassword({
          email: data.email,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <div className="flex grow justify-center items-center px-8 max-w-full">
      <form onSubmit={handleSubmit(handleSendResetPasswordLink)}>
        <LoaderOverlay isLoading={isLoading}>
          <Card elevation={10} className="rounded-xl w-[552px] max-w-full">
            <CardHeader
              title="Reset password"
              subheader={<span>Link to reset password will be send to your email address.</span>}
            />
            <CardContent>
              <div className="flex flex-col gap-4">
                {isSuccessResetPassword && (
                  <Alert severity="success">Link to reset password has been sent to your email address.</Alert>
                )}
                {isErrorResetPassword && <Alert severity="error">{errorResetPassword?.message}</Alert>}
                <Controller
                  control={control}
                  name="email"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <TextField
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      value={value}
                      onChange={onChange}
                      disabled={isSuccessResetPassword}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      variant="outlined"
                      label={t('label.email')}
                      placeholder={t('placeholder.email')}
                    />
                  )}
                />
                <LoadingButton
                  loading={isLoading}
                  disabled={isSuccessResetPassword}
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                >
                  {t('button.sendResetPasswordLink')}
                </LoadingButton>
              </div>
            </CardContent>
          </Card>
        </LoaderOverlay>
      </form>
    </div>
  );
};

export default ForgotPasswordPage;
